import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  FormControl,
  FormHelperText,
  FormLabel,
  Grid,
  IconButton,
  Input,
  Modal,
  ModalClose,
  ModalDialog,
  Option,
  Select,
  Stack,
  Textarea,
  Tooltip,
  Typography,
} from '@mui/joy';

import {
  Add,
  Delete,
  Edit,
  PersonAddAlt1,
  Search,
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';
import TimePicker from 'react-time-picker';
import 'react-time-picker/dist/TimePicker.css';
import 'react-clock/dist/Clock.css';
import { shiftTypeService } from '@/services/shiftTypeService';
import { ShiftType } from '@/types/shiftType';
import { useAuth } from '@/context/AuthContext';
import toast from 'react-hot-toast';
import AssignShiftTypeEmployeesModal from '@/components/AssignShiftTypeEmployeesModal';

interface ShiftTypeFormData {
  name: string;
  description: string;
  color: string;
  default_start_time: string;
  default_end_time: string;
}

const initialFormData: ShiftTypeFormData = {
  name: '',
  description: '',
  color: '#3B82F6',
  default_start_time: '',
  default_end_time: '',
};

type SortKey = 'name_asc' | 'name_desc' | 'start_time' | 'recent';
type StatusFilter = 'active' | 'disabled' | 'all';

const SORT_OPTIONS: { value: SortKey; label: string }[] = [
  { value: 'name_asc', label: 'Name (A–Z)' },
  { value: 'name_desc', label: 'Name (Z–A)' },
  { value: 'start_time', label: 'Start time' },
  { value: 'recent', label: 'Recently created' },
];

const STATUS_OPTIONS: { value: StatusFilter; label: string }[] = [
  { value: 'active', label: 'Active' },
  { value: 'disabled', label: 'Disabled' },
  { value: 'all', label: 'All' },
];

export default function ShiftTypesPage() {
  const { permissions } = useAuth();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingShiftType, setEditingShiftType] = useState<ShiftType | null>(null);
  const [formData, setFormData] = useState<ShiftTypeFormData>(initialFormData);
  const [errors, setErrors] = useState<Partial<ShiftTypeFormData>>({});
  const [shiftTypes, setShiftTypes] = useState<ShiftType[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('name_asc');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('active');
  const [assignTarget, setAssignTarget] = useState<ShiftType | null>(null);

  const hasPermission = (feature: string, action: string) =>
    permissions.includes(`${feature}.${action}`);

  const loadShiftTypes = async (includeInactive: boolean) => {
    try {
      const data = await shiftTypeService.getAllShiftTypes(includeInactive);
      setShiftTypes(data);
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to load shift types');
    }
  };

  useEffect(() => {
    const includeInactive = statusFilter !== 'active';
    setIsLoading(true);
    loadShiftTypes(includeInactive).finally(() => setIsLoading(false));
  }, [statusFilter]);

  const refresh = () => loadShiftTypes(statusFilter !== 'active');

  const handleOpenDialog = (shiftType?: ShiftType) => {
    if (shiftType) {
      setEditingShiftType(shiftType);
      setFormData({
        name: shiftType.name,
        description: shiftType.description || '',
        color: shiftType.color,
        default_start_time: shiftType.default_start_time || '',
        default_end_time: shiftType.default_end_time || '',
      });
    } else {
      setEditingShiftType(null);
      setFormData(initialFormData);
    }
    setErrors({});
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setEditingShiftType(null);
    setFormData(initialFormData);
    setErrors({});
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<ShiftTypeFormData> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.color) {
      newErrors.color = 'Color is required';
    } else if (!/^#[0-9A-F]{6}$/i.test(formData.color)) {
      newErrors.color = 'Invalid color format (use #RRGGBB)';
    }

    if (
      formData.default_start_time &&
      !/^([01]?[0-9]|2[0-3]):[0-5][0-9]:[0-5][0-9]$/.test(formData.default_start_time)
    ) {
      newErrors.default_start_time = 'Invalid time format (use HH:MM:SS)';
    }

    if (
      formData.default_end_time &&
      !/^([01]?[0-9]|2[0-3]):[0-5][0-9]:[0-5][0-9]$/.test(formData.default_end_time)
    ) {
      newErrors.default_end_time = 'Invalid time format (use HH:MM:SS)';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    const submitData = {
      name: formData.name.trim(),
      description: formData.description.trim() || undefined,
      color: formData.color,
      default_start_time: formData.default_start_time || undefined,
      default_end_time: formData.default_end_time || undefined,
    };

    try {
      if (editingShiftType) {
        await shiftTypeService.updateShiftType(editingShiftType.id, submitData);
        toast.success('Shift type updated successfully');
      } else {
        await shiftTypeService.createShiftType(submitData);
        toast.success('Shift type created successfully');
      }

      await refresh();
      handleCloseDialog();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to save shift type');
    }
  };

  const handleDelete = async (shiftType: ShiftType) => {
    if (!window.confirm(`Delete "${shiftType.name}"? This cannot be undone.`)) return;
    try {
      await shiftTypeService.deleteShiftType(shiftType.id);
      toast.success('Shift type deleted');
      await refresh();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to delete shift type');
    }
  };

  const handleToggleActive = async (shiftType: ShiftType) => {
    const nextActive = !shiftType.is_active;
    try {
      await shiftTypeService.setActive(shiftType.id, nextActive);
      toast.success(
        nextActive
          ? 'Shift type enabled'
          : 'Shift type disabled — hidden from scheduling',
      );
      await refresh();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to update shift type');
    }
  };

  const formatTimeRange = (start?: string, end?: string) => {
    const trim = (t?: string) => (t ? t.slice(0, 5) : null);
    const s = trim(start);
    const e = trim(end);
    if (!s && !e) return 'No default time';
    if (s && e) return `${s} – ${e}`;
    return s || e || '';
  };

  const visibleShiftTypes = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    let list = shiftTypes.filter((st) => {
      if (statusFilter === 'active' && !st.is_active) return false;
      if (statusFilter === 'disabled' && st.is_active) return false;
      if (!term) return true;
      return (
        st.name.toLowerCase().includes(term) ||
        (st.description && st.description.toLowerCase().includes(term))
      );
    });

    list = [...list].sort((a, b) => {
      switch (sortKey) {
        case 'name_asc':
          return a.name.localeCompare(b.name);
        case 'name_desc':
          return b.name.localeCompare(a.name);
        case 'start_time': {
          const sa = a.default_start_time || '99:99:99';
          const sb = b.default_start_time || '99:99:99';
          return sa.localeCompare(sb);
        }
        case 'recent':
          return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
        default:
          return 0;
      }
    });

    return list;
  }, [shiftTypes, searchTerm, sortKey, statusFilter]);

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Typography>Loading shift types...</Typography>
      </Box>
    );
  }

  const canAssign = hasPermission('shift_types', 'assign');
  const canEdit = hasPermission('shift_types', 'edit');
  const canDelete = hasPermission('shift_types', 'delete');

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography level="h1">Shift Types</Typography>
        {hasPermission('shift_types', 'create') && (
          <Button startDecorator={<Add />} onClick={() => handleOpenDialog()} size="sm">
            Add Shift Type
          </Button>
        )}
      </Stack>

      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        mb={3}
      >
        <Input
          placeholder="Search shift types..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          startDecorator={<Search />}
          size="md"
          sx={{ flex: 1, minWidth: 0 }}
        />
        <FormControl size="md" sx={{ minWidth: 180 }}>
          <FormLabel sx={{ fontSize: 'xs' }}>Sort by</FormLabel>
          <Select value={sortKey} onChange={(_, v) => v && setSortKey(v)}>
            {SORT_OPTIONS.map((o) => (
              <Option key={o.value} value={o.value}>
                {o.label}
              </Option>
            ))}
          </Select>
        </FormControl>
        <FormControl size="md" sx={{ minWidth: 160 }}>
          <FormLabel sx={{ fontSize: 'xs' }}>Status</FormLabel>
          <Select value={statusFilter} onChange={(_, v) => v && setStatusFilter(v)}>
            {STATUS_OPTIONS.map((o) => (
              <Option key={o.value} value={o.value}>
                {o.label}
              </Option>
            ))}
          </Select>
        </FormControl>
      </Stack>

      {visibleShiftTypes.length === 0 ? (
        <Card variant="soft">
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <Typography level="title-md" color="neutral">
              No shift types match your filters
            </Typography>
            <Typography level="body-sm" color="neutral">
              Try adjusting your search, sort, or status filter.
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <Grid container spacing={2}>
          {visibleShiftTypes.map((shiftType) => {
            const disabled = !shiftType.is_active;
            const assigned = shiftType.assigned_count ?? 0;
            const inUse = !!shiftType.in_use;

            return (
              <Grid xs={12} sm={6} md={4} key={shiftType.id}>
                <Card
                  variant={disabled ? 'soft' : 'outlined'}
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    opacity: disabled ? 0.7 : 1,
                  }}
                >
                  <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="flex-start"
                      spacing={1}
                      mb={1.5}
                    >
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Stack direction="row" spacing={1} alignItems="center" mb={0.5}>
                          <Typography level="title-md" noWrap>
                            {shiftType.name}
                          </Typography>
                          {disabled && (
                            <Chip size="sm" color="neutral" variant="solid">
                              Disabled
                            </Chip>
                          )}
                        </Stack>
                        <Typography
                          level="body-sm"
                          color="neutral"
                          sx={{
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            minHeight: 40,
                          }}
                        >
                          {shiftType.description || 'No description'}
                        </Typography>
                      </Box>
                      <Stack direction="row" spacing={0.5}>
                        {canEdit && (
                          <Tooltip title="Edit">
                            <IconButton
                              size="sm"
                              variant="plain"
                              onClick={() => handleOpenDialog(shiftType)}
                            >
                              <Edit />
                            </IconButton>
                          </Tooltip>
                        )}
                        {inUse && canEdit ? (
                          <Tooltip title={disabled ? 'Enable' : 'Disable for future use'}>
                            <IconButton
                              size="sm"
                              variant="plain"
                              color={disabled ? 'success' : 'warning'}
                              onClick={() => handleToggleActive(shiftType)}
                            >
                              {disabled ? <Visibility /> : <VisibilityOff />}
                            </IconButton>
                          </Tooltip>
                        ) : (
                          canDelete && (
                            <Tooltip title="Delete">
                              <IconButton
                                size="sm"
                                variant="plain"
                                color="danger"
                                onClick={() => handleDelete(shiftType)}
                              >
                                <Delete />
                              </IconButton>
                            </Tooltip>
                          )
                        )}
                      </Stack>
                    </Stack>

                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      mb={2}
                    >
                      <Box
                        sx={{
                          width: 14,
                          height: 14,
                          borderRadius: '50%',
                          backgroundColor: shiftType.color,
                          border: '1px solid',
                          borderColor: 'divider',
                          flexShrink: 0,
                        }}
                      />
                      <Typography level="body-sm" color="neutral">
                        {formatTimeRange(shiftType.default_start_time, shiftType.default_end_time)}
                      </Typography>
                    </Stack>

                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                      mt="auto"
                      spacing={1}
                    >
                      {assigned === 0 ? (
                        <Typography level="body-xs" color="warning" fontStyle="italic">
                          No users assigned
                        </Typography>
                      ) : (
                        <Chip size="sm" variant="soft">
                          {assigned} {assigned === 1 ? 'user' : 'users'} assigned
                        </Chip>
                      )}
                      {canAssign && (
                        <Button
                          size="sm"
                          variant="outlined"
                          startDecorator={<PersonAddAlt1 />}
                          onClick={() => setAssignTarget(shiftType)}
                        >
                          Add
                        </Button>
                      )}
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* Create / Edit Modal */}
      <Modal open={isDialogOpen} onClose={handleCloseDialog}>
        <ModalDialog size="md" sx={{ width: { xs: '95vw', sm: 560 } }}>
          <ModalClose />
          <Typography level="h4" mb={2}>
            {editingShiftType ? 'Edit Shift Type' : 'Create Shift Type'}
          </Typography>

          <Grid container spacing={2}>
            <Grid xs={12}>
              <FormControl error={!!errors.name}>
                <FormLabel>Name</FormLabel>
                <Input
                  placeholder="e.g. Morning Shift"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                />
                {errors.name && <FormHelperText>{errors.name}</FormHelperText>}
              </FormControl>
            </Grid>

            <Grid xs={12}>
              <FormControl>
                <FormLabel>Description</FormLabel>
                <Textarea
                  minRows={2}
                  maxRows={4}
                  placeholder="Optional description of this shift type"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                />
              </FormControl>
            </Grid>

            <Grid xs={12} sm={4}>
              <FormControl error={!!errors.color}>
                <FormLabel>Color</FormLabel>
                <Input
                  value={formData.color}
                  onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                  startDecorator={
                    <Box
                      component="input"
                      type="color"
                      value={formData.color}
                      onChange={(e) =>
                        setFormData({ ...formData, color: (e.target as HTMLInputElement).value })
                      }
                      sx={{
                        width: 28,
                        height: 28,
                        border: 'none',
                        padding: 0,
                        background: 'transparent',
                        cursor: 'pointer',
                      }}
                    />
                  }
                />
                {errors.color && <FormHelperText>{errors.color}</FormHelperText>}
              </FormControl>
            </Grid>

            <Grid xs={6} sm={4}>
              <FormControl error={!!errors.default_start_time}>
                <FormLabel>Start time</FormLabel>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    height: 36,
                    px: 1,
                    border: '1px solid',
                    borderColor: errors.default_start_time ? 'danger.500' : 'neutral.outlinedBorder',
                    borderRadius: 'sm',
                    backgroundColor: 'background.surface',
                    '& .react-time-picker': { width: '100%', border: 'none' },
                    '& .react-time-picker__wrapper': { border: 'none' },
                  }}
                >
                  <TimePicker
                    value={formData.default_start_time || null}
                    onChange={(value) => {
                      const timeString = value ? `${value}:00` : '';
                      setFormData({ ...formData, default_start_time: timeString });
                    }}
                    format="hh:mm a"
                    clearIcon={null}
                    disableClock
                  />
                </Box>
                {errors.default_start_time && (
                  <FormHelperText>{errors.default_start_time}</FormHelperText>
                )}
              </FormControl>
            </Grid>

            <Grid xs={6} sm={4}>
              <FormControl error={!!errors.default_end_time}>
                <FormLabel>End time</FormLabel>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    height: 36,
                    px: 1,
                    border: '1px solid',
                    borderColor: errors.default_end_time ? 'danger.500' : 'neutral.outlinedBorder',
                    borderRadius: 'sm',
                    backgroundColor: 'background.surface',
                    '& .react-time-picker': { width: '100%', border: 'none' },
                    '& .react-time-picker__wrapper': { border: 'none' },
                  }}
                >
                  <TimePicker
                    value={formData.default_end_time || null}
                    onChange={(value) => {
                      const timeString = value ? `${value}:00` : '';
                      setFormData({ ...formData, default_end_time: timeString });
                    }}
                    format="hh:mm a"
                    clearIcon={null}
                    disableClock
                  />
                </Box>
                {errors.default_end_time && (
                  <FormHelperText>{errors.default_end_time}</FormHelperText>
                )}
              </FormControl>
            </Grid>
          </Grid>

          <Stack direction="row" spacing={1} justifyContent="flex-end" mt={3}>
            <Button variant="plain" color="neutral" onClick={handleCloseDialog}>
              Cancel
            </Button>
            <Button onClick={handleSubmit}>
              {editingShiftType ? 'Update' : 'Create'}
            </Button>
          </Stack>
        </ModalDialog>
      </Modal>

      <AssignShiftTypeEmployeesModal
        open={!!assignTarget}
        shiftType={assignTarget}
        onClose={() => setAssignTarget(null)}
        onSaved={refresh}
      />
    </Box>
  );
}
